import React, { useEffect, useState } from 'react';
import { CONTACT_EMAIL } from '../../constants/index';
import { useSelector } from 'react-redux';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import {
    Box,
    Typography,
    Button,
    Container,
    Paper,
    Divider,
    Modal,
    Backdrop,
    Fade
} from '@material-ui/core';
import useForm, { FormContext } from 'react-hook-form';
import history from '../../history';

import useModal from '../hooks/useModal';
import TextField from '../form/TextField';

import client from '../../utils/client';
import ContactPreview from './ContactPreview';
import Alert from '../../utils/alert';

const useStyles = makeStyles((theme) => ({
    userForm: {
        position: 'relative',
        margin: 'auto',
        maxWidth: '60rem',
        [theme.breakpoints.down('sm')]: {
            width: '100%',
        },
    },
    heading: {
        textAlign: 'center',
        color: '#52bbf8',
        marginBottom: '5rem',
        marginTop: '5rem',
        fontSize: '3rem',
        fontWeight: 600,
        [theme.breakpoints.down('sm')]: {
            fontSize: '2.4rem',
        },
    },
    description: {
        fontSize: '1.6rem',
        lineHeight: 1.6,
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(3),
        '& a': {
            color: theme.palette.primary.main,
            textDecoration: 'underline',
        },
    },
    formContainer: {
        backgroundColor: theme.palette.background.paper,
        padding: theme.spacing(3),
        borderRadius: theme.shape.borderRadius,
        boxShadow: theme.shadows[1],
    },
    submitButton: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        fontSize: '1.6rem',
        padding: theme.spacing(1.5, 4),
        margin: '0 auto',
        display: 'block',
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
        },
    },
    submitButtonContainer: {
        marginTop: theme.spacing(3),
    },
    modal: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    modalContent: {
        backgroundColor: theme.palette.background.paper,
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(3),
        outline: 'none',
        maxWidth: '60rem',
        width: '90%',
        maxHeight: '90vh',
        overflow: 'auto',
    },
    modalTitle: {
        fontSize: '1.8rem',
        fontWeight: 600,
        marginBottom: theme.spacing(2),
        color: theme.palette.text.primary,
    },
    buttonContainer: {
        display: 'flex',
        gap: theme.spacing(2),
        justifyContent: 'center',
        marginTop: theme.spacing(3),
    },
    okButton: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
        },
    },
    cancelButton: {
        backgroundColor: '#ff3860',
        color: 'white',
        '&:hover': {
            backgroundColor: '#e02e4f',
        },
    },
}));

const ContactForm = props => {
    const classes = useStyles();
    const theme = useTheme();
    const user = useSelector(state => state.user.currentUser);
    const [formData, setFormData] = useState({});
    const methods = useForm(); // initialise the hook
    const { setValue, handleSubmit } = methods;
    const [openModal, closeModal, modalIsOpen] = useModal();

    useEffect(() => {
        if (user) {
            setValue('email', user.email);
            setValue('name', user.name);
        }
    }, [user, setValue])

    const onSubmit = data => {
        setFormData(data);
        openModal();
    }

    const sendEmail = () => {
        client.post('/feedback', formData)
            .then(res => {
                Alert.success("メッセージを送信しました");
                history.push('/');
            })
            .catch(error => {
                Alert.error(error);
            })
    }

    return (
        <Container className={classes.userForm}>
            <Typography variant="h1" className={classes.heading}>
                お問い合わせ
            </Typography>

            <Typography className={classes.description}>
                ニュージーワークスにご関心をお持ちいただき誠にありがとうございます。<br />
                以下のお問い合わせフォームより、ご意見やご相談などお問合わせください。<br />
                ご利用中に見つけた不具合の報告等もこちらで受け付けております。<br />
                また、メールでの問い合わせも可能です。(<a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>)<br /><br />
                お問合せ内容の確認後、担当者よりご連絡させていただきます。
            </Typography>

            <Paper className={classes.formContainer} elevation={1}>
                <FormContext {...methods} >
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <TextField
                            label="件名"
                            type="text"
                            placeholder="件名を入力してください"
                            name="title"
                            registerOptions={{ required: true, maxLength: 100 }}
                        />
                        <TextField
                            label="お名前"
                            type="text"
                            placeholder="名前を入力してください"
                            name="name"
                            registerOptions={{ required: true, maxLength: 100 }}
                        />
                        <TextField
                            label="メールアドレス"
                            type="email"
                            placeholder="Ｅメールアドレスを入力してください"
                            name="email"
                            registerOptions={{ required: true, maxLength: 100 }}
                        />
                        <TextField
                            label="本文"
                            type="textarea"
                            name="message"
                            rows={15}
                            cols={20}
                            registerOptions={{ required: true, maxLength: 10000 }}
                        />
                        <Box className={classes.submitButtonContainer}>
                            <Button
                                type="submit"
                                className={classes.submitButton}
                                variant="contained"
                                size="large"
                            >
                                送信
                            </Button>
                        </Box>
                    </form>
                </FormContext>
            </Paper>

            <Modal
                open={modalIsOpen}
                onClose={closeModal}
                className={classes.modal}
                BackdropComponent={Backdrop}
                BackdropProps={{
                    timeout: 500,
                }}
            >
                <Fade in={modalIsOpen}>
                    <Box className={classes.modalContent}>
                        <Typography className={classes.modalTitle}>
                            以下の内容で送信します。よろしいですか？
                        </Typography>
                        <Divider style={{ margin: '1rem 0' }} />
                        <ContactPreview formData={formData} />
                        <Divider style={{ margin: '1rem 0' }} />
                        <Box className={classes.buttonContainer}>
                            <Button
                                type="submit"
                                onClick={sendEmail}
                                className={classes.okButton}
                                variant="contained"
                            >
                                OK
                            </Button>
                            <Button
                                type="button"
                                className={classes.cancelButton}
                                onClick={closeModal}
                                variant="contained"
                            >
                                キャンセル
                            </Button>
                        </Box>
                    </Box>
                </Fade>
            </Modal>
        </Container>
    )
}

export default ContactForm
